import {
  AncestorIdData,
  AncestorNameData,
  FolderAncestorsData,
  FolderId,
  FolderInfoData,
  FolderNameData,
  maybeSplitFolderContents,
  FolderContentsData,
} from 'modules/domains/foldering';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { useGraphql } from 'utils/hooks/useGraphql';
import { AssetSubfolder, Asset, Pagination } from '../types';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { FetchAssetsAndFoldersQuery } from 'api/queries/creativeLibrary';

export type FetchAssetFolderQueryResponse = {
  assetFolder: AssetSubfolder &
    FolderInfoData &
    FolderAncestorsData<AncestorIdData & AncestorNameData> &
    FolderContentsData<AssetSubfolder, Asset>;
};

export type FolderContents = {
  childFolders: AssetSubfolder[];
  childAssets: Asset[];
};

export type FolderData = {
  folderData: FolderNameData &
    FolderAncestorsData<AncestorIdData & AncestorNameData> & {
      id: FolderId | null;
    } & FolderNameData;
};

export type AssetFolderData = FolderContents & FolderData;

type useFetchAssetFolderQueryResult = AssetFolderData &
  FolderInfoData & {
    isLoading: boolean;
    hasError: boolean;
    refetch: () => void;
  };

export type AssetFolderRequestConfig = {
  folderId?: FolderId;
  search?: string;
  sort?: { sortBy: string; sortDirection: SORT_DIRECTIONS };
  pagination?: Pagination;
  recursive?: boolean;
  assetFilterInfo?: {
    assetIds?: number[] | null;
    createdByUserId?: string[] | null;
    updatedByUserId?: string[] | null;
    mimeType?: string[] | null;
    size?: string[] | null;
  };
};

export function useFetchAssetsAndFoldersQuery(
  queryParams: AssetFolderRequestConfig,
  options?: Record<string, any>,
): useFetchAssetFolderQueryResult {
  const {
    data,
    error: hasError,
    ...response
  } = useGraphql<FetchAssetFolderQueryResponse>(FetchAssetsAndFoldersQuery, {
    variables: { ...queryParams },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    ...options,
  });

  // Split the content into folders and assets
  const [folders, assets] = maybeSplitFolderContents<AssetSubfolder, Asset>(
    GRAPHQL_TYPENAMES.IMAGE_ASSET,
    data?.assetFolder?.content,
  );

  return {
    ...response,
    hasError,
    childFolders: folders,
    childAssets: assets,
    folderData: {
      name: data?.assetFolder?.name ?? '',
      id: data?.assetFolder?.id ?? null,
      ancestors: data?.assetFolder?.ancestors ?? [],
    },
    info: data?.assetFolder?.info ?? {
      count: 0,
      limit: 20,
      offset: 0,
      page: 1,
      __typename: GRAPHQL_TYPENAMES.INFO,
    },
  };
}
